import type {
  GameContext,
  GameInstance,
  GameModule,
  PreloadContext,
} from '@arcraz/arcadia-types';
import { InterceptorInstance } from './instance.js';
import { PALETTE_HINT } from './palette.js';

/**
 * INTERCEPTOR — second cartridge.
 *
 * Top-down dodge-and-shoot pursuit. Spy Hunter heritage; original mechanics.
 * MVP (sc-2815): continuous lateral steering, civilian traffic, Switchblade
 * enemy with telegraphed lunges, score = distance + enemies passed.
 *
 * Follow-ups (under epic 2811): Enforcer + Doctor Torpedo enemies, terrain
 * transitions, Weapons Van pickups, front gun + projectile system,
 * civilian-no-shoot penalty.
 */
const module_: GameModule = {
  id: 'interceptor',
  title: 'INTERCEPTOR',
  manufacturer: 'ARCADIA AMUSEMENTS',
  year: 1984,
  // MVP has no projectile system yet — gameplay is pure dodge-pursuit.
  // Bump to 'shooter' when the front gun + projectile system lands
  // (follow-up under epic 2811).
  genre: 'racing',
  resolution: { w: 480, h: 270 },
  pixelated: true,
  paletteHint: PALETTE_HINT,
  validatorVersion: 1,
  controls: [
    { keys: 'ARROWS / WASD', action: 'STEER + ACCEL' },
    { keys: 'UP / Z', action: 'ACCELERATE' },
    { keys: 'DOWN', action: 'BRAKE' },
    { keys: 'Z', action: 'RESTART (AT GAME OVER)' },
  ],

  async preload(ctx: PreloadContext): Promise<void> {
    // Switchblade warning blip: short rising chirp, played ONCE when a
    // Switchblade enters its lunge-warning state. Quiet enough that
    // multiple overlapping Switchblades aren't a wall of beeps.
    ctx.registerSfx('warn', {
      wave: 'square',
      pitch: [{ t: 0, f: 660 }, { t: 80, f: 990 }],
      gain: [{ t: 0, g: 0 }, { t: 5, g: 0.18 }, { t: 80, g: 0.0 }],
    });
    // Crash bang: short noise burst.
    ctx.registerSfx('crash', {
      wave: 'noise',
      pitch: [{ t: 0, f: 80 }, { t: 200, f: 35 }],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.55 }, { t: 200, g: 0.0 }],
    });
    // Zone-up: three-note ascending fanfare.
    ctx.registerSfx('zone-up', {
      wave: 'square',
      pitch: [
        { t: 0, f: 440 },
        { t: 90, f: 550 },
        { t: 180, f: 660 },
      ],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.3 }, { t: 180, g: 0.0 }],
    });
  },

  init(ctx: GameContext): GameInstance {
    return new InterceptorInstance(ctx);
  },
};

export default module_;
